// filepath: frontend/src/components/layout/NotificationPanel.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, CheckCheck, Bell, Mail, AlertTriangle, Clock, DollarSign,
  UserX, Zap, ListTodo, ChevronRight, RefreshCw, Link2, Link2Off,
  Inbox, Loader, Link, Check, Search, BedDouble,
} from 'lucide-react';
import { useNotificationStore } from '@/store/notificationStore';
import { api } from '@/lib/api';
import { Shipment, PaginatedResult } from '@/types';

const ACCENT  = '#E47527';
const TEAL    = '#00A9BC';
const INDIGO  = '#818cf8';
const GREEN   = '#22c55e';
const AMBER   = '#f59e0b';
const RED     = '#ef4444';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ActionPoint {
  id: string;
  type: 'task' | 'stale' | 'costing' | 'unassigned' | 'escalated' | 'signal' | 'signal_hoarding' | 'delegation_pending' | 'task_overdue';
  priority: 'urgent' | 'high' | 'normal';
  title: string;
  body: string;
  link: string;
  entity_id: string;
}

interface EmailMessage {
  id: string;
  threadId: string;
  subject: string;
  from: string;
  snippet: string;
  date: string;
  isUnread: boolean;
  isStarred: boolean;
}

interface EmailStatus {
  connected: boolean;
  provider?: string;
  email?: string;
}

// ── Config ────────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  task:               { icon: ListTodo,      color: INDIGO,  label: 'Task'         },
  stale:              { icon: Clock,         color: AMBER,   label: 'Stalled'      },
  costing:            { icon: DollarSign,    color: TEAL,    label: 'Costing'      },
  unassigned:         { icon: UserX,         color: AMBER,   label: 'Unassigned'   },
  escalated:          { icon: Zap,           color: RED,     label: 'Escalated'    },
  signal:             { icon: Mail,          color: INDIGO,  label: 'Email'        },
  signal_hoarding:    { icon: AlertTriangle, color: RED,     label: 'Unlinked'     },
  delegation_pending: { icon: Clock,         color: AMBER,   label: 'No response'  },
  task_overdue:       { icon: AlertTriangle, color: RED,     label: 'Overdue'      },
};

const PRIORITY_DOT: Record<string, string> = {
  urgent: RED,
  high:   AMBER,
  normal: TEAL,
};

function timeAgo(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(ms / 60_000);
  const h = Math.floor(ms / 3_600_000);
  const d = Math.floor(ms / 86_400_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

function fromName(from: string): string {
  const match = from.match(/^"?([^"<]+)"?\s*</);
  return match ? match[1].trim() : from.split('@')[0];
}

// ── Attach-to-shipment dropdown ───────────────────────────────────────────────

interface AttachDropdownProps {
  messageId: string;
  subject: string;
  from: string;
  snippet: string;
  onAttached: () => void;
}

function AttachDropdown({ messageId, subject, from, snippet, onAttached }: AttachDropdownProps) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // AI suggestions — only fire when dropdown opens and there's email content
  const { data: suggestions = [], isFetching: suggestLoading } = useQuery<Shipment[]>({
    queryKey: ['signal-suggest', messageId],
    queryFn: () => api.get('/signals/suggest', { params: { subject, from, body: snippet.slice(0, 400) } }).then(r => r.data),
    enabled: open && (!!subject || !!snippet),
    staleTime: Infinity,
  });

  const { data: shipmentPage } = useQuery<PaginatedResult<Shipment>>({
    queryKey: ['attach-search', search],
    queryFn: () => api.get('/shipments', { params: { division: 'brokerage', limit: 20, search: search || undefined } }).then(r => r.data),
    enabled: open,
    staleTime: 30_000,
  });

  const attach = useMutation({
    mutationFn: (shipmentId: string) =>
      api.post('/signals', {
        type: 'email',
        email_message_id: messageId,
        subject,
        source: from,
        body_preview: snippet.slice(0, 300),
        shipment_id: shipmentId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-messages'] });
      qc.invalidateQueries({ queryKey: ['signal-attached-ids'] });
      setOpen(false);
      onAttached();
    },
  });

  const allShipments = shipmentPage?.data ?? [];
  // Exclude suggestions from the manual list to avoid duplicates
  const suggestIds = new Set(suggestions.map(s => s.id));
  const manualShipments = search ? allShipments.filter(s => !suggestIds.has(s.id)) : allShipments.filter(s => !suggestIds.has(s.id));

  const ShipmentRow = ({ s, label }: { s: Shipment; label?: string }) => (
    <button
      key={s.id}
      onClick={() => attach.mutate(s.id)}
      disabled={attach.isPending}
      className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-xs transition-colors hover:bg-white/[0.05] disabled:opacity-40"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.75)' }}
    >
      <span className="font-mono text-[11px] flex-shrink-0" style={{ color: TEAL }}>{s.c_number ?? s.reference}</span>
      <span className="truncate flex-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{(s as any).client?.name}</span>
      {label && <span className="text-[9px] px-1 rounded" style={{ background: `${ACCENT}22`, color: ACCENT }}>{label}</span>}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md transition-colors hover:bg-white/10 flex-shrink-0"
        style={{ color: TEAL, border: `1px solid ${TEAL}25`, background: `${TEAL}08` }}
        title="Attach to shipment"
      >
        <Link size={10} />
        Attach
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full mt-1.5 z-50 rounded-xl overflow-hidden"
            style={{ width: 300, background: '#0d2137', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}
          >
            <div className="px-3 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="text-[10px] uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>Link to shipment</p>
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Search size={10} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
                <input
                  autoFocus
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search reference or client…"
                  className="w-full bg-transparent outline-none text-[11px] text-white placeholder:text-white/25"
                />
              </div>
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {/* AI suggestions section */}
              {suggestLoading && (
                <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <Loader size={10} className="animate-spin" style={{ color: ACCENT }} />
                  <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>AI matching…</span>
                </div>
              )}
              {!suggestLoading && suggestions.length > 0 && (
                <>
                  <div className="px-3 pt-2 pb-1">
                    <span className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: ACCENT }}>AI suggested</span>
                  </div>
                  {suggestions.map(s => <ShipmentRow key={s.id} s={s} label="match" />)}
                  {(manualShipments.length > 0 || search) && (
                    <div className="px-3 pt-2 pb-1" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <span className="text-[9px] uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.25)' }}>All entries</span>
                    </div>
                  )}
                </>
              )}
              {/* Manual list */}
              {manualShipments.length === 0 && !suggestLoading && suggestions.length === 0
                ? <p className="text-[11px] text-center py-5" style={{ color: 'rgba(255,255,255,0.25)' }}>No entries found</p>
                : manualShipments.map(s => <ShipmentRow key={s.id} s={s} />)
              }
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props { open: boolean; onClose: () => void; }

// ── Component ─────────────────────────────────────────────────────────────────

export function NotificationPanel({ open, onClose }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { notifications: wsNotifications, setNotifications, markAllRead, markRead } = useNotificationStore();
  const [tab, setTab] = useState<'actions' | 'email' | 'history'>('actions');

  // ── Action points ──────────────────────────────────────────────────────────
  const { data: actionPoints = [], isLoading: apLoading, refetch: refetchAP } = useQuery<ActionPoint[]>({
    queryKey: ['action-points'],
    queryFn: () => api.get('/notifications/action-points').then(r => r.data),
    enabled: open,
    staleTime: 30_000,
    refetchInterval: open ? 60_000 : false,
  });

  // ── Notification history (API-backed) ─────────────────────────────────────
  const { data: apiNotifications = [], isLoading: historyLoading } = useQuery({
    queryKey: ['notifications-history'],
    queryFn: () => api.get('/notifications').then(r => r.data),
    enabled: open && tab === 'history',
    staleTime: 30_000,
  });

  // Merge API history with in-session WebSocket notifications (deduplicated by id)
  const notifications = [
    ...wsNotifications,
    ...(apiNotifications as any[]).filter((a: any) => !wsNotifications.some(w => w.id === a.id)),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // ── Email status ───────────────────────────────────────────────────────────
  const { data: emailStatus, isLoading: emailStatusLoading } = useQuery<EmailStatus>({
    queryKey: ['email-status'],
    queryFn: () => api.get('/notifications/email/status').then(r => r.data),
    enabled: open,
    staleTime: 60_000,
  });

  // ── Email messages ─────────────────────────────────────────────────────────
  const { data: emailMessages = [], isLoading: emailLoading, refetch: refetchEmail } = useQuery<EmailMessage[]>({
    queryKey: ['email-messages'],
    queryFn: () => api.get('/notifications/email/messages').then(r => r.data),
    enabled: open && tab === 'email' && !!emailStatus?.connected,
    staleTime: 120_000,
  });

  // ── Already-attached email IDs (to show attached badge) ───────────────────
  const { data: attachedIds = {}, refetch: refetchAttached } = useQuery<Record<string, { shipment_id: string | null; status: string }>>({
    queryKey: ['signal-attached-ids'],
    queryFn: () => api.get('/signals/attached-ids').then(r => r.data),
    enabled: open && tab === 'email',
    staleTime: 60_000,
  });

  // ── Disconnect email ───────────────────────────────────────────────────────
  const disconnect = useMutation({
    mutationFn: () => api.delete('/notifications/email/disconnect'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-status'] }),
  });

  // ── Gmail OAuth popup ──────────────────────────────────────────────────────
  const connectGmail = useCallback(async () => {
    const { data } = await api.get('/notifications/email/auth-url');
    const popup = window.open(data.url, 'gmail-oauth', 'width=520,height=640,left=200,top=100');
    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'EMAIL_OAUTH_RESULT') return;
      window.removeEventListener('message', handler);
      popup?.close();
      if (e.data.success) {
        qc.invalidateQueries({ queryKey: ['email-status'] });
        qc.invalidateQueries({ queryKey: ['email-messages'] });
      }
    };
    window.addEventListener('message', handler);
  }, [qc]);

  // ── Notifications (history tab) ────────────────────────────────────────────
  const handleMarkAll = async () => {
    await api.post('/notifications/read-all').catch(() => {});
    markAllRead();
  };
  const handleMarkOne = async (id: string) => {
    await api.post(`/notifications/${id}/read`).catch(() => {});
    markRead(id);
  };

  const urgentCount = actionPoints.filter(p => p.priority === 'urgent').length;
  const totalActions = actionPoints.length;

  return (
    <AnimatePresence>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />

          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.97 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.97 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-14 right-4 z-50 flex flex-col rounded-2xl overflow-hidden"
            style={{
              width: 400,
              maxHeight: 'calc(100vh - 80px)',
              background: '#0a1929',
              boxShadow: '0 0 0 1px rgba(255,255,255,0.09), 0 16px 48px rgba(0,0,0,0.6)',
            }}
            role="dialog"
            aria-label="Action Centre"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3.5 flex-shrink-0"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: urgentCount > 0 ? `${RED}18` : `${ACCENT}18`, border: `1px solid ${urgentCount > 0 ? RED : ACCENT}30` }}>
                  <Bell size={13} style={{ color: urgentCount > 0 ? RED : ACCENT }} />
                </div>
                <div>
                  <h2 className="text-sm font-semibold" style={{ color: 'white', letterSpacing: '0.02em' }}>Action Centre</h2>
                  {totalActions > 0 && (
                    <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)', marginTop: -1 }}>
                      {totalActions} item{totalActions !== 1 ? 's' : ''} need attention
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => { refetchAP(); if (tab === 'email') refetchEmail(); }}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Refresh">
                  <RefreshCw size={12} style={{ color: 'rgba(255,255,255,0.4)' }} />
                </button>
                <button onClick={onClose}>
                  <X size={15} style={{ color: 'rgba(255,255,255,0.4)' }} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-shrink-0 px-3 pt-2.5 gap-1"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {([
                { id: 'actions', label: 'Actions', count: totalActions },
                { id: 'email',   label: 'Inbox',   count: emailMessages.filter(m => m.isUnread).length },
                { id: 'history', label: 'History',  count: notifications.filter(n => !n.is_read).length },
              ] as const).map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-all relative"
                  style={{ color: tab === t.id ? 'white' : 'rgba(255,255,255,0.38)' }}>
                  {t.label}
                  {t.count > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums"
                      style={{ background: tab === t.id ? ACCENT : 'rgba(255,255,255,0.1)', color: 'white', minWidth: 18, textAlign: 'center' }}>
                      {t.count}
                    </span>
                  )}
                  {tab === t.id && (
                    <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                      style={{ background: ACCENT }} />
                  )}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>

              {/* ── ACTIONS TAB ─────────────────────────────────────────── */}
              {tab === 'actions' && (
                <div>
                  {apLoading ? (
                    <div className="flex items-center justify-center py-12 gap-2">
                      <Loader size={14} className="animate-spin" style={{ color: ACCENT }} />
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Computing action items…</span>
                    </div>
                  ) : actionPoints.length === 0 ? (
                    <div className="flex flex-col items-center py-14 gap-3">
                      <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
                        style={{ background: `${GREEN}12`, border: `1px solid ${GREEN}25` }}>
                        <CheckCheck size={18} style={{ color: GREEN }} />
                      </div>
                      <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>All clear</p>
                      <p className="text-xs text-center max-w-[200px]" style={{ color: 'rgba(255,255,255,0.28)' }}>
                        No action items right now. Check back later.
                      </p>
                    </div>
                  ) : (
                    <div className="py-1">
                      {(['urgent', 'high', 'normal'] as const).map(priority => {
                        const items = actionPoints.filter(p => p.priority === priority);
                        if (!items.length) return null;
                        const priorityLabel = priority === 'urgent' ? 'Urgent' : priority === 'high' ? 'High Priority' : 'To Do';
                        return (
                          <div key={priority}>
                            <div className="px-4 py-2 flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ background: PRIORITY_DOT[priority] }} />
                              <span className="text-[10px] uppercase tracking-widest font-semibold"
                                style={{ color: 'rgba(255,255,255,0.3)' }}>{priorityLabel}</span>
                            </div>
                            {items.map(point => {
                              const cfg = TYPE_CONFIG[point.type] ?? TYPE_CONFIG.task;
                              const Icon = cfg.icon;
                              return (
                                <button key={point.id} onClick={() => { navigate(point.link); onClose(); }}
                                  className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.04] group"
                                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                  <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                                    style={{ background: `${cfg.color}18`, border: `1px solid ${cfg.color}30` }}>
                                    <Icon size={13} style={{ color: cfg.color }} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate" style={{ color: 'rgba(255,255,255,0.88)' }}>
                                      {point.title}
                                    </p>
                                    <p className="text-[11px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>
                                      {point.body}
                                    </p>
                                  </div>
                                  <ChevronRight size={12} className="flex-shrink-0 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ color: 'rgba(255,255,255,0.4)' }} />
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── EMAIL TAB ───────────────────────────────────────────── */}
              {tab === 'email' && (
                <div>
                  {emailStatusLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader size={14} className="animate-spin" style={{ color: ACCENT }} />
                    </div>
                  ) : !emailStatus?.connected ? (
                    // Not connected
                    <div className="flex flex-col items-center py-10 px-6 gap-5">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <Mail size={24} style={{ color: 'rgba(255,255,255,0.25)' }} />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Connect your inbox</p>
                        <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.35)' }}>
                          See emails that need a reply or action alongside your system notifications.
                        </p>
                      </div>
                      <div className="w-full flex flex-col gap-2">
                        <button onClick={connectGmail}
                          className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-xl text-sm font-medium transition-opacity hover:opacity-85"
                          style={{ background: 'rgba(255,255,255,0.07)', color: 'white', border: '1px solid rgba(255,255,255,0.12)' }}>
                          {/* Google G logo */}
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Connect Gmail
                        </button>
                        <button disabled
                          className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-xl text-sm font-medium opacity-40 cursor-not-allowed"
                          style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
                          {/* Microsoft logo */}
                          <svg viewBox="0 0 21 21" width="16" height="16">
                            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                          </svg>
                          Connect Outlook <span className="text-[10px] ml-1 opacity-70">(soon)</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Connected
                    <div>
                      {/* Connection status bar */}
                      <div className="flex items-center justify-between px-4 py-3"
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(34,197,94,0.04)' }}>
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                            {emailStatus.email}
                          </span>
                        </div>
                        <button onClick={() => disconnect.mutate()}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg hover:bg-white/10 transition-colors"
                          style={{ color: 'rgba(255,255,255,0.3)' }}
                          title="Disconnect">
                          <Link2Off size={11} />
                          Disconnect
                        </button>
                      </div>

                      {emailLoading ? (
                        <div className="flex items-center justify-center py-10 gap-2">
                          <Loader size={14} className="animate-spin" style={{ color: ACCENT }} />
                          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Fetching inbox…</span>
                        </div>
                      ) : emailMessages.length === 0 ? (
                        <div className="flex flex-col items-center py-12 gap-3">
                          <Inbox size={28} style={{ color: 'rgba(255,255,255,0.15)' }} />
                          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>Inbox is clear</p>
                        </div>
                      ) : (
                        <div>
                          {emailMessages.map(msg => {
                            const alreadyAttached = attachedIds[msg.id];
                            return (
                            <div key={msg.id}
                              className="px-4 py-3 transition-colors hover:bg-white/[0.03]"
                              style={{
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                background: msg.isUnread ? 'rgba(228,117,39,0.04)' : 'transparent',
                              }}>
                              <div className="flex items-start gap-2.5">
                                {/* Unread/starred dot */}
                                <div className="flex-shrink-0 mt-1.5">
                                  {alreadyAttached
                                    ? <Check size={10} style={{ color: GREEN }} />
                                    : msg.isUnread
                                    ? <div className="w-1.5 h-1.5 rounded-full" style={{ background: ACCENT }} />
                                    : msg.isStarred
                                    ? <div className="w-1.5 h-1.5 rounded-full" style={{ background: AMBER }} />
                                    : <div className="w-1.5 h-1.5" />
                                  }
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2 mb-0.5">
                                    <span className="text-[11px] font-semibold truncate" style={{ color: msg.isUnread ? 'white' : 'rgba(255,255,255,0.55)' }}>
                                      {fromName(msg.from)}
                                    </span>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
                                        {timeAgo(msg.date)}
                                      </span>
                                      {alreadyAttached
                                        ? <span className="text-[10px] px-1.5 py-0.5 rounded-md" style={{ background: `${GREEN}12`, color: GREEN, border: `1px solid ${GREEN}25` }}>Attached</span>
                                        : <AttachDropdown
                                            messageId={msg.id}
                                            subject={msg.subject}
                                            from={msg.from}
                                            snippet={msg.snippet}
                                            onAttached={() => refetchAttached()}
                                          />
                                      }
                                    </div>
                                  </div>
                                  <p className="text-xs truncate mb-0.5" style={{ color: msg.isUnread ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.45)' }}>
                                    {msg.subject}
                                  </p>
                                  <p className="text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.28)' }}>
                                    {msg.snippet}
                                  </p>
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── HISTORY TAB ─────────────────────────────────────────── */}
              {tab === 'history' && (
                <div>
                  {/* Mark all read */}
                  {notifications.some(n => !n.is_read) && (
                    <div className="flex justify-end px-4 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <button onClick={handleMarkAll}
                        className="flex items-center gap-1 text-xs hover:opacity-80 transition-opacity"
                        style={{ color: 'rgba(255,255,255,0.4)' }}>
                        <CheckCheck size={12} />
                        Mark all read
                      </button>
                    </div>
                  )}

                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center py-14 gap-3">
                      <Bell size={28} style={{ color: 'rgba(255,255,255,0.1)' }} />
                      <p className="text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>No notifications</p>
                    </div>
                  ) : (
                    notifications.map(notif => (
                      <div key={notif.id}
                        onClick={() => !notif.is_read && handleMarkOne(notif.id)}
                        className="px-4 py-3 transition-colors hover:bg-white/[0.03] cursor-pointer"
                        style={{
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                          background: notif.is_read ? 'transparent' : 'rgba(228,117,39,0.04)',
                        }}>
                        <div className="flex items-start gap-2.5">
                          {!notif.is_read && (
                            <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: ACCENT }} />
                          )}
                          <div className={!notif.is_read ? '' : 'ml-4'}>
                            <p className="text-xs font-semibold mb-0.5" style={{ color: 'rgba(255,255,255,0.85)' }}>{notif.title}</p>
                            <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{notif.body}</p>
                            <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.22)' }}>
                              {timeAgo(notif.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
